<template>
  <div class="feeding-chart-page">
    <!-- 日期选择器 -->
    <div class="date-picker-section">
      <van-cell title="日期范围" :value="dateRangeText" @click="showDatePicker = true" is-link class="date-cell" />
      <van-calendar
        v-model:show="showDatePicker"
        type="range"
        :min-date="minDate"
        :max-date="maxDate"
        :default-date="defaultDate"
        @confirm="onDateConfirm"
        @select="onDateSelect"
        color="#05C2CB"
        :poppable="true"
        :round="true"
        position="bottom"
        :show-title="true"
        :show-confirm="false"
        class="feeding-calendar"
      >
        <template #title>
          <div class="calendar-header">
            <span class="calendar-confirm" @click="confirmDateSelection">确认</span>
            <span class="calendar-title">选择日期范围</span>
            <span class="calendar-close" @click="showDatePicker = false">✕</span>
          </div>
        </template>
      </van-calendar>
    </div>

    <!-- 加载状态 -->
    <div v-if="loading" class="loading-section">
      <van-loading type="spinner" color="#05C2CB">加载中...</van-loading>
    </div>

    <!-- 错误状态 -->
    <div v-else-if="error" class="error-section">
      <p>{{ error }}</p>
      <van-button type="primary" size="small" @click="fetchData">重新加载</van-button>
    </div>

    <!-- 图表内容 -->
    <div v-else class="chart-content">
      <!-- 统计卡片 -->
      <div class="stats-cards" v-if="statistics">
        <div class="stat-card">
          <div class="stat-value">{{ statistics.total_feeds || 0 }}</div>
          <div class="stat-label">总喂养次数</div>
        </div>
        <div class="stat-card">
          <div class="stat-value">{{ statistics.total_volume || 0 }}<span class="unit">ml</span></div>
          <div class="stat-label">总奶量</div>
        </div>
        <div class="stat-card">
          <div class="stat-value">{{ statistics.avg_volume || 0 }}<span class="unit">ml</span></div>
          <div class="stat-label">平均每顿</div>
        </div>
      </div>

      <!-- 每日总奶量图表 -->
      <div class="chart-section">
        <div class="section-title">每日总奶量</div>
        <div ref="dailyChart" class="chart-container"></div>
      </div>

      <!-- 每顿奶量图表 -->
      <div class="chart-section">
        <div class="section-title">每顿奶量明细</div>
        <div ref="feedingChart" class="chart-container"></div>
      </div>

      <!-- 空数据提示 -->
      <div v-if="!hasData" class="empty-section">
        <van-empty description="暂无喂养记录" />
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import * as echarts from 'echarts';
import { TOKEN, buildPublicApiUrl } from '@/utils/config';

export default {
    name: 'FeedingChart',
    data () {
        return {
            loading: false,
            error: null,
            showDatePicker: false,
            startDate: null,
            endDate: null,
            minDate: new Date(2024, 0, 1),
            maxDate: new Date(),
            defaultDate: null,
            feedingRecords: [],
            dailySummary: [],
            statistics: null,
            dailyChartInstance: null,
            feedingChartInstance: null,
            token: '',
            tempSelectedDates: null  // 临时存储日历选中的日期
        };
    },
    computed: {
        dateRangeText () {
            if (this.startDate && this.endDate) {
                return `${this.formatDate(this.startDate)} - ${this.formatDate(this.endDate)}`;
            }
            return '请选择日期';
        },
        hasData () {
            return this.feedingRecords.length > 0 || this.dailySummary.some(d => d.total_volume_ml > 0);
        }
    },
    mounted () {
    // 获取 token
        let type = this.$route.query.type;
        if (type === 'test') {
            this.token = TOKEN;
        } else {
            this.token = this.$route.query.token;
        }

        // 设置默认日期范围（最近7天）
        this.endDate = new Date();
        this.startDate = new Date();
        this.startDate.setDate(this.startDate.getDate() - 6);
        this.defaultDate = [this.startDate, this.endDate];

        this.fetchData();
    },
    beforeDestroy () {
        if (this.dailyChartInstance) {
            this.dailyChartInstance.dispose();
        }
        if (this.feedingChartInstance) {
            this.feedingChartInstance.dispose();
        }
    },
    methods: {
        formatDate (date) {
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            return `${year}-${month}-${day}`;
        },

        onDateConfirm (dates) {
            this.startDate = dates[0];
            this.endDate = dates[1];
            this.showDatePicker = false;
            this.fetchData();
        },

        onDateSelect (dates) {
            // 存储临时选中的日期（range 模式下 dates 是数组）
            this.tempSelectedDates = dates;
        },

        confirmDateSelection () {
            // 自定义确认按钮点击
            if (this.tempSelectedDates && this.tempSelectedDates.length === 2) {
                this.startDate = this.tempSelectedDates[0];
                this.endDate = this.tempSelectedDates[1];
                this.showDatePicker = false;
                this.fetchData();
            } else {
                // 如果没有选择完整日期范围，提示用户
                this.$toast && this.$toast('请选择开始和结束日期');
            }
        },

        async fetchData () {
            this.loading = true;
            this.error = null;

            try {
                const response = await axios.get(buildPublicApiUrl('/feedingchart/chart'), {
                    headers: {
                        Authorization: 'bearer ' + this.token,
                        channel: 1000
                    },
                    params: {
                        start_date: this.formatDate(this.startDate),
                        end_date: this.formatDate(this.endDate)
                    }
                });

                if (response.data.success) {
                    const result = response.data.result;
                    this.feedingRecords = result.feeding_records || [];
                    this.dailySummary = result.daily_summary || [];
                    this.statistics = result.statistics || {};
                } else {
                    this.error = response.data.errors?.message || '获取数据失败';
                }
            } catch (err) {
                console.error('获取奶量数据失败:', err);
                this.error = '网络错误，请检查网络连接';
            } finally {
                this.loading = false;
                // 等待 DOM 更新后再渲染图表（loading=false 后图表容器才可见）
                this.$nextTick(() => {
                    this.renderDailyChart();
                    this.renderFeedingChart();
                });
            }
        },

        renderDailyChart () {
            if (!this.$refs.dailyChart) {return;}

            if (this.dailyChartInstance) {
                this.dailyChartInstance.dispose();
            }

            this.dailyChartInstance = echarts.init(this.$refs.dailyChart);

            const dates = this.dailySummary.map(d => d.date_label);
            const volumes = this.dailySummary.map(d => d.total_volume_ml);

            const option = {
                tooltip: {
                    trigger: 'axis',
                    formatter: '{b}<br/>总奶量: {c} ml'
                },
                grid: {
                    left: '3%',
                    right: '4%',
                    bottom: '3%',
                    top: '10%',
                    containLabel: true
                },
                xAxis: {
                    type: 'category',
                    data: dates,
                    axisLabel: {
                        fontSize: 11,
                        color: '#666'
                    },
                    axisLine: {
                        lineStyle: { color: '#e0e0e0' }
                    }
                },
                yAxis: {
                    type: 'value',
                    name: 'ml',
                    nameTextStyle: {
                        fontSize: 11,
                        color: '#999'
                    },
                    axisLabel: {
                        fontSize: 11,
                        color: '#666'
                    },
                    splitLine: {
                        lineStyle: { color: '#f0f0f0' }
                    }
                },
                series: [{
                    name: '每日总奶量',
                    type: 'bar',
                    data: volumes,
                    itemStyle: {
                        color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                            { offset: 0, color: '#05C2CB' },
                            { offset: 1, color: '#0ea5e9' }
                        ]),
                        borderRadius: [4, 4, 0, 0]
                    },
                    barWidth: '50%',
                    label: {
                        show: true,
                        position: 'top',
                        fontSize: 10,
                        color: '#666',
                        formatter: (params) => params.value > 0 ? params.value : ''
                    }
                }]
            };

            this.dailyChartInstance.setOption(option);
        },

        renderFeedingChart () {
            if (!this.$refs.feedingChart) {return;}

            if (this.feedingChartInstance) {
                this.feedingChartInstance.dispose();
            }

            this.feedingChartInstance = echarts.init(this.$refs.feedingChart);

            const times = this.feedingRecords.map(r => r.time_label);
            const volumes = this.feedingRecords.map(r => r.volume_ml);
            const feedTypes = this.feedingRecords.map(r => this.normalizeFeedType(r.feed_type));

            const option = {
                tooltip: {
                    trigger: 'axis',
                    formatter: (params) => {
                        const item = params && params.length ? params[0] : null;
                        if (!item) { return ''; }
                        const type = feedTypes[item.dataIndex] || '喂养';
                        return `${item.axisValue}<br/>${type}: ${item.value} ml`;
                    }
                },
                grid: {
                    left: '3%',
                    right: '4%',
                    bottom: '15%',
                    top: '10%',
                    containLabel: true
                },
                dataZoom: [{
                    type: 'inside',
                    start: 0,
                    end: 100
                }, {
                    start: 0,
                    end: 100,
                    height: 20,
                    bottom: 0
                }],
                xAxis: {
                    type: 'category',
                    data: times,
                    axisLabel: {
                        fontSize: 10,
                        color: '#666',
                        rotate: 45
                    },
                    axisLine: {
                        lineStyle: { color: '#e0e0e0' }
                    }
                },
                yAxis: {
                    type: 'value',
                    name: 'ml',
                    nameTextStyle: {
                        fontSize: 11,
                        color: '#999'
                    },
                    axisLabel: {
                        fontSize: 11,
                        color: '#666'
                    },
                    splitLine: {
                        lineStyle: { color: '#f0f0f0' }
                    }
                },
                series: [{
                    name: '每顿奶量',
                    type: 'bar',
                    data: volumes,
                    itemStyle: {
                        color: (params) => this.colorForFeedType(feedTypes[params.dataIndex]),
                        borderRadius: [3, 3, 0, 0]
                    },
                    barWidth: '60%'
                }]
            };

            this.feedingChartInstance.setOption(option);
        },

        normalizeFeedType (feedType) {
            const value = String(feedType || '');
            if (value.includes('瓶') || value === 'breast_milk_bottle') {
                return '母乳瓶喂';
            }
            if (value.includes('亲') || value === 'breast_milk' || value === 'breast') {
                return '母乳亲喂';
            }
            if (value.includes('配方') || value === 'formula') {
                return '配方奶';
            }
            return value || '喂养';
        },

        colorForFeedType (feedType) {
            if (feedType === '母乳亲喂') {
                return '#05C2CB';
            }
            if (feedType === '母乳瓶喂') {
                return '#D4C34D';
            }
            if (feedType === '配方奶') {
                return '#f59e0b';
            }
            return '#05C2CB';
        }
    }
};
</script>

<style scoped>
.feeding-chart-page {
  min-height: 100vh;
  background: #f5f7fa;
  padding-bottom: 40px;
}

.date-picker-section {
  margin: 0 12px 12px 12px;
  border-radius: 8px;
  overflow: hidden;
}

.date-picker-section .date-cell /deep/ .van-cell__title {
  flex: none;
  width: 70px;
}

.date-picker-section .date-cell /deep/ .van-cell__value {
  flex: 1;
  text-align: right;
}

.loading-section,
.error-section {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
}

.error-section p {
  color: #ef4444;
  margin-bottom: 16px;
}

.chart-content {
  padding: 0 12px;
}

.stats-cards {
  display: flex;
  gap: 10px;
  margin-bottom: 16px;
}

.stat-card {
  flex: 1;
  background: #fff;
  border-radius: 10px;
  padding: 14px 10px;
  text-align: center;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
}

.stat-value {
  font-size: 22px;
  font-weight: 700;
  color: #05C2CB;
}

.stat-value .unit {
  font-size: 12px;
  font-weight: 400;
  color: #999;
  margin-left: 2px;
}

.stat-label {
  font-size: 12px;
  color: #666;
  margin-top: 4px;
}

.chart-section {
  background: #fff;
  border-radius: 10px;
  padding: 16px;
  margin-bottom: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
}

.section-title {
  font-size: 15px;
  font-weight: 600;
  color: #333;
  margin-bottom: 12px;
  padding-left: 8px;
  border-left: 3px solid #05C2CB;
}

.chart-container {
  width: 100%;
  height: 260px;
}

.empty-section {
  padding: 40px 0;
}
</style>

<style>
/* 覆盖 van-calendar 弹出层样式，解决 App WebView 中确认按钮显示不出来的问题 */
.van-calendar__popup {
  max-height: 70% !important;
  height: 70% !important;
}

.van-calendar__popup .van-calendar {
  height: 100% !important;
  display: flex;
  flex-direction: column;
}

.van-calendar__popup .van-calendar__body {
  flex: 1;
  overflow-y: auto;
  min-height: 0;
}

.van-calendar__popup .van-calendar__footer {
  flex-shrink: 0;
  padding-bottom: constant(safe-area-inset-bottom);
  padding-bottom: env(safe-area-inset-bottom);
}

/* 隐藏 van-calendar 默认的关闭按钮 */
.van-calendar__close-icon,
.van-popup__close-icon,
.van-calendar__header-title + .van-icon,
.van-calendar .van-icon-cross {
  display: none !important;
}

/* 自定义日历头部样式 */
.calendar-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  border-bottom: 1px solid #ebedf0;
}

.calendar-close {
  font-size: 18px;
  color: #969799;
  padding: 4px 8px;
  cursor: pointer;
}

.calendar-title {
  font-size: 16px;
  font-weight: 500;
  color: #323233;
}

.calendar-confirm {
  font-size: 14px;
  color: #05C2CB;
  font-weight: 500;
  padding: 4px 8px;
  cursor: pointer;
}

.calendar-confirm:active {
  opacity: 0.7;
}
</style>
